const PARTNER_CONFIG_STORAGE_KEY = 'heygg_partner_config';
const PARTNER_CONFIG_CHECK_KEY = 'heygg_partner_config_check';
const PARTNER_CONFIG_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours
const PARTNER_USER_STORAGE_KEY = 'heygg_partner_user';

export interface PartnerConfig {
  param_name: string;
  partner_name: string;
}

export interface PartnerUser {
  param_name: string;
  partner_user_id: string;
}

function setLocalStorage(key: string, value: any) {
  if (value === null) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(
      key,
      typeof value !== 'string' ? JSON.stringify(value) : value,
    );
  }
}

function getLocalStorage(key: string) {
  const value = localStorage.getItem(key);
  return value ? (typeof value === 'string' ? value : JSON.parse(value)) : null;
}

export async function fetchPartnerConfig(apiBaseUrl: string): Promise<PartnerConfig[]> {
  const now = Date.now();
  const lastCheck = getLocalStorage(PARTNER_CONFIG_CHECK_KEY) || 0;
  const cachedConfig = getLocalStorage(PARTNER_CONFIG_STORAGE_KEY);
  
  // Use cached config if within 24 hours
  if (cachedConfig && (now - lastCheck) < PARTNER_CONFIG_INTERVAL) {
    console.log('[HEYGG COMMON] Using cached partner config');
    return cachedConfig;
  }
  
  try {
    console.log('[HEYGG COMMON] Fetching fresh partner config');
    const response = await fetch(`${apiBaseUrl}/api/partner-config`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch partner config');
    }
    
    const config = await response.json();
    setLocalStorage(PARTNER_CONFIG_STORAGE_KEY, config);
    setLocalStorage(PARTNER_CONFIG_CHECK_KEY, now);
    return config;
  } catch (err) {
    console.warn('[HEYGG COMMON] Failed to fetch partner config:', err);
    // Return cached config if available, otherwise empty array
    return cachedConfig || [];
  }
}

export function getPartnerUserFromUrl(partnerConfigs: PartnerConfig[]): PartnerUser | null {
  const searchParams = new URLSearchParams(window.location.search);
  
  for (const config of partnerConfigs) {
    const partnerUserId = searchParams.get(config.param_name);
    if (partnerUserId) {
      return {
        param_name: config.param_name,
        partner_user_id: partnerUserId,
      };
    }
  }
  
  return null;
}

export function savePartnerUser(partnerUser: PartnerUser | null): void {
  setLocalStorage(PARTNER_USER_STORAGE_KEY, partnerUser);
}

export function getStoredPartnerUser(): PartnerUser | null {
  return getLocalStorage(PARTNER_USER_STORAGE_KEY);
}

export function clearPartnerUser(): void {
  setLocalStorage(PARTNER_USER_STORAGE_KEY, null);
}

export function cleanPartnerParamsFromUrl(partnerConfigs: PartnerConfig[], currentPartner?: PartnerUser | null): URLSearchParams {
  const searchParams = new URLSearchParams(window.location.search);
  
  // Clean current partner param
  if (currentPartner) {
    searchParams.delete(currentPartner.param_name);
  }
  
  // Clean any other partner params that might be in the URL
  for (const config of partnerConfigs) {
    searchParams.delete(config.param_name);
  }
  
  return searchParams;
}